/**
 * React Hook for WebSocket Real-time Updates
 * Provides easy integration with WebSocket service in React components
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import websocketService from '../services/websocket';

export const useWebSocket = (userId, options = {}) => {
  const {
    autoConnect = true,
    baseUrl = 'ws://localhost:8000',
    onConnect = null,
    onDisconnect = null,
    onError = null,
    onNewAlert = null,
    onAlertUpdate = null,
    onSystemMessage = null,
    onCameraStatusUpdate = null
  } = options;

  const [connectionState, setConnectionState] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState(null);
  
  const handlersRef = useRef({
    onConnect,
    onDisconnect,
    onError,
    onNewAlert,
    onAlertUpdate,
    onSystemMessage,
    onCameraStatusUpdate
  });

  // Update handlers when they change
  useEffect(() => {
    handlersRef.current = {
      onConnect,
      onDisconnect,
      onError,
      onNewAlert,
      onAlertUpdate,
      onSystemMessage,
      onCameraStatusUpdate
    };
  }, [onConnect, onDisconnect, onError, onNewAlert, onAlertUpdate, onSystemMessage, onCameraStatusUpdate]);

  // Handle connection state changes
  const handleConnectionStateChange = useCallback((newState) => {
    setConnectionState(newState);
    setError(null);
  }, []);

  // Handle new alerts
  const handleNewAlert = useCallback((alertData) => {
    console.log('🚨 [HOOK] New alert received:', alertData);
    setLastMessage({ type: 'new_alert', data: alertData, timestamp: new Date() });
    
    // Add to alerts list (prepend to show newest first)
    setAlerts(prev => [alertData, ...prev]);
    
    // Call custom handler if provided
    if (handlersRef.current.onNewAlert) {
      handlersRef.current.onNewAlert(alertData);
    }
  }, []);

  // Handle alert updates
  const handleAlertUpdate = useCallback((alertData) => {
    console.log('📝 [HOOK] Alert update received:', alertData);
    setLastMessage({ type: 'alert_update', data: alertData, timestamp: new Date() });
    
    // Update alert in list
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertData.id ? { ...alert, ...alertData } : alert
      )
    );
    
    // Call custom handler if provided
    if (handlersRef.current.onAlertUpdate) {
      handlersRef.current.onAlertUpdate(alertData);
    }
  }, []);

  // Handle system messages
  const handleSystemMessage = useCallback((messageData) => {
    console.log('📢 [HOOK] System message received:', messageData);
    setLastMessage({ type: 'system_message', data: messageData, timestamp: new Date() });
    
    // Call custom handler if provided
    if (handlersRef.current.onSystemMessage) {
      handlersRef.current.onSystemMessage(messageData);
    }
  }, []);

  // Handle camera status updates
  const handleCameraStatusUpdate = useCallback((statusData) => {
    console.log('📡 [HOOK] Camera status update received:', statusData);
    setLastMessage({ type: 'camera_status_update', data: statusData, timestamp: new Date() });
    
    // Call custom handler if provided
    if (handlersRef.current.onCameraStatusUpdate) {
      handlersRef.current.onCameraStatusUpdate(statusData);
    }
  }, []);

  // Handle connection events
  const handleConnectionEstablished = useCallback((data) => {
    console.log('🔌 [HOOK] Connection established:', data);
    handleConnectionStateChange('connected');
    
    if (handlersRef.current.onConnect) {
      handlersRef.current.onConnect(data);
    }
  }, [handleConnectionStateChange]);

  const handleConnectionClosed = useCallback((data) => {
    console.log('🔌 [HOOK] Connection closed:', data);
    handleConnectionStateChange('disconnected');
    
    if (handlersRef.current.onDisconnect) {
      handlersRef.current.onDisconnect(data);
    }
  }, [handleConnectionStateChange]);

  const handleConnectionError = useCallback((data) => {
    console.log('❌ [HOOK] Connection error:', data);
    setError(data.error);
    handleConnectionStateChange('error');
    
    if (handlersRef.current.onError) {
      handlersRef.current.onError(data);
    }
  }, [handleConnectionStateChange]);

  const handleReconnectFailed = useCallback((data) => {
    console.log('❌ [HOOK] Reconnect failed:', data);
    setError('Failed to reconnect to real-time updates');
    handleConnectionStateChange('error');
  }, [handleConnectionStateChange]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!userId) {
      console.warn('⚠️ [HOOK] Cannot connect - no userId provided');
      return;
    }

    console.log('🔌 [HOOK] Connecting to WebSocket...');
    handleConnectionStateChange('connecting');
    
    websocketService.connect(userId, baseUrl);
  }, [userId, baseUrl, handleConnectionStateChange]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 [HOOK] Disconnecting from WebSocket...');
    websocketService.disconnect();
    handleConnectionStateChange('disconnected');
  }, [handleConnectionStateChange]);

  // Send message through WebSocket
  const sendMessage = useCallback((message) => {
    websocketService.send(message);
  }, []);

  // Request connection status
  const requestConnectionStatus = useCallback(() => {
    websocketService.requestConnectionStatus();
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (!userId) return;

    // Register event handlers
    websocketService.on('connection_established', handleConnectionEstablished);
    websocketService.on('connection_closed', handleConnectionClosed);
    websocketService.on('connection_error', handleConnectionError);
    websocketService.on('reconnect_failed', handleReconnectFailed);
    websocketService.on('new_alert', handleNewAlert);
    websocketService.on('alert_update', handleAlertUpdate);
    websocketService.on('system_message', handleSystemMessage);
    websocketService.on('camera_status_update', handleCameraStatusUpdate);

    // Auto-connect if enabled
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      websocketService.off('connection_established', handleConnectionEstablished);
      websocketService.off('connection_closed', handleConnectionClosed);
      websocketService.off('connection_error', handleConnectionError);
      websocketService.off('reconnect_failed', handleReconnectFailed);
      websocketService.off('new_alert', handleNewAlert);
      websocketService.off('alert_update', handleAlertUpdate);
      websocketService.off('system_message', handleSystemMessage);
      websocketService.off('camera_status_update', handleCameraStatusUpdate);
      
      if (autoConnect) {
        disconnect();
      }
    };
  }, [userId, autoConnect, connect, disconnect, handleConnectionEstablished, handleConnectionClosed, handleConnectionError, handleReconnectFailed, handleNewAlert, handleAlertUpdate, handleSystemMessage, handleCameraStatusUpdate]);

  // Update connection state from service
  useEffect(() => {
    const updateConnectionState = () => {
      setConnectionState(websocketService.getConnectionState());
    };

    // Initial state
    updateConnectionState();

    // Update state periodically
    const interval = setInterval(updateConnectionState, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    // Connection state
    connectionState,
    isConnected: websocketService.isConnected(),
    error,
    
    // Data
    alerts,
    lastMessage,
    
    // Actions
    connect,
    disconnect,
    sendMessage,
    requestConnectionStatus,
    
    // Utility
    clearAlerts: () => setAlerts([]),
    clearError: () => setError(null)
  };
};

export default useWebSocket;
